import PennText from "@/components/ui/text/PennText";
import { formatMoney } from "@/utils/cost-display";
import type { GlobalUsefulLifeTotals } from "@/utils/comparison-global-totals";

/**
 * Global cost-of-ownership rollup: configured machine investment plus
 * useful-life labour and quality cost across all assigned applications.
 */
export function ComparisonGlobalTotals({
  totals,
}: {
  totals: GlobalUsefulLifeTotals;
}) {
  const currency = totals.currencyCode ?? undefined;
  const yearsLabel = formatYears(totals.usefulLifeYears);
  const perYear = (v: number) => `${formatMoney(v, currency)} / year`;

  return (
    <div className="mt-4 rounded-2xl border border-gray-200 bg-gray-50/60 p-4">
      <PennText
        as="p"
        size="xs"
        color="blue_grey"
        font="semibold"
        className="uppercase mb-3"
      >
        Total cost of ownership ({yearsLabel} year
        {yearsLabel === "1" ? "" : "s"})
      </PennText>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <TotalCard
          label="Your selected configuration"
          value={formatMoney(totals.machineTotal, currency)}
          sub="One-time investment"
        />
        <TotalCard
          label="Labour cost"
          value={formatMoney(totals.labourTotal, currency)}
          sub={perYear(totals.labourPerYear)}
        />
        <TotalCard
          label="Quality cost"
          value={formatMoney(totals.qualityTotal, currency)}
          sub={perYear(totals.qualityPerYear)}
        />
        <TotalCard
          label="Total cost"
          value={formatMoney(totals.grandTotal, currency)}
          sub={perYear(totals.grandPerYear)}
          emphasis
        />
      </div>
    </div>
  );
}

function TotalCard({
  label,
  value,
  sub,
  emphasis = false,
}: {
  label: string;
  value: string;
  sub?: string;
  emphasis?: boolean;
}) {
  return (
    <div
      className={`rounded-xl border bg-white p-3 ${
        emphasis ? "border-penn-300" : "border-gray-200"
      }`}
    >
      <PennText as="p" size="xs" color="blue_grey">
        {label}
      </PennText>
      <p
        className={`text-lg font-semibold ${
          emphasis ? "text-penn-700" : "text-blue_grey1"
        }`}
      >
        {value}
      </p>
      {sub ? (
        <PennText as="p" size="xs" color="blue_grey" className="mt-0.5">
          {sub}
        </PennText>
      ) : null}
    </div>
  );
}

/** POC `fmtYears` (L11780): trim a trailing ".0" so "7.0" → "7". */
function formatYears(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return "7";
  const rounded = Math.round(value * 100) / 100;
  const s = String(rounded);
  return s.endsWith(".0") ? s.slice(0, -2) : s;
}
